import { useCallback, useEffect, useRef, useState } from "react";
import { Layout } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getNavFor } from "@/lib/nav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileText, Globe, Trash2, BookOpen, Plus, Save, Loader2, ArrowUp, ArrowDown, GripVertical, ListOrdered, Pencil, RefreshCw } from "lucide-react";

function has(user, perm) { return user?.role === "super_admin" || (user?.permissions || []).includes(perm); }

const TOPICS = ["Standard Dosage", "Contraindications", "Drug Interactions", "Side Effects", "Disease Management"];

export default function HODPortal() {
  const { user } = useAuth();
  const [resources, setResources] = useState([]);
  const [sources, setSources] = useState([]);
  const [openUpload, setOpenUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [up, setUp] = useState({ title: "", category: "Drug Information", precedence: 30, usage_rules: "", is_primary: false, page_offset: 0, drug_content_start: 0, interactions_start: 0 });
  const fileRef = useRef();
  const [openSrc, setOpenSrc] = useState(false);
  const [srcForm, setSrcForm] = useState({ name: "", url: "", precedence: 50, enabled: true, notes: "" });
  const [editRes, setEditRes] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [topicOrders, setTopicOrders] = useState({});
  const [savingTopic, setSavingTopic] = useState(false);

  const load = useCallback(async () => {
    const [r, s, st] = await Promise.all([api.get("/resources"), api.get("/web-sources"), api.get("/settings")]);
    setResources(r.data); setSources(s.data);
    // build topic orders: saved order first, then any remaining active resources by precedence
    const saved = (st.data && st.data.topic_order) || {};
    const activeSorted = r.data.filter((x) => x.is_active).sort((a, b) => a.precedence - b.precedence).map((x) => x.id);
    const next = {};
    TOPICS.forEach((t) => {
      const savedIds = (saved[t] || []).filter((id) => activeSorted.includes(id));
      const rest = activeSorted.filter((id) => !savedIds.includes(id));
      next[t] = [...savedIds, ...rest];
    });
    setTopicOrders(next);
  }, []);
  useEffect(() => { load(); }, [load]);

  const moveTopic = (topic, idx, delta) => {
    setTopicOrders((prev) => {
      const arr = [...(prev[topic] || [])];
      const ni = idx + delta;
      if (ni < 0 || ni >= arr.length) return prev;
      [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
      return { ...prev, [topic]: arr };
    });
  };

  const saveTopicOrder = async () => {
    setSavingTopic(true);
    try { await api.put("/settings", { topic_order: topicOrders }); toast.success("Topic ordering saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingTopic(false); }
  };

  const doUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a PDF file"); return; }
    if (!up.title) { toast.error("Add a title"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", up.title);
    fd.append("category", up.category);
    fd.append("precedence", String(up.precedence));
    fd.append("usage_rules", up.usage_rules);
    fd.append("is_primary", String(up.is_primary));
    fd.append("page_offset", String(up.page_offset || 0));
    fd.append("drug_content_start", String(up.drug_content_start || 0));
    fd.append("interactions_start", String(up.interactions_start || 0));
    try {
      const { data } = await api.post("/resources/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded — ${data.page_count} pages indexed`);
      setOpenUpload(false); setUp({ title: "", category: "Drug Information", precedence: 30, usage_rules: "", is_primary: false, page_offset: 0, drug_content_start: 0, interactions_start: 0 });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const [reindexing, setReindexing] = useState(null);
  const reindexResource = async (r) => {
    setReindexing(r.id);
    try {
      const { data } = await api.post(`/resources/${r.id}/reindex`);
      toast.success(`Re-indexed — ${data.page_count} pages read`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Re-index failed"); }
    finally { setReindexing(null); }
  };

  const changePrecedence = async (r, delta) => {
    await api.put(`/resources/${r.id}`, { precedence: Math.max(1, (r.precedence || 50) + delta) });
    load();
  };
  const delResource = async (r) => { if (!window.confirm(`Remove ${r.title}?`)) return; await api.delete(`/resources/${r.id}`); load(); };
  const toggleResource = async (r) => { await api.put(`/resources/${r.id}`, { is_active: !r.is_active }); load(); };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.put(`/resources/${editRes.id}`, {
        title: editRes.title, category: editRes.category,
        precedence: Number(editRes.precedence), usage_rules: editRes.usage_rules,
        is_primary: !!editRes.is_primary,
        page_offset: Number(editRes.page_offset) || 0,
        drug_content_start: Number(editRes.drug_content_start) || 0,
        interactions_start: Number(editRes.interactions_start) || 0,
      });
      toast.success("Reference updated");
      setEditRes(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };

  const addSource = async () => {
    try { await api.post("/web-sources", srcForm); toast.success("Source added to allowlist"); setOpenSrc(false); setSrcForm({ name: "", url: "", precedence: 50, enabled: true, notes: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleSource = async (s) => { await api.put(`/web-sources/${s.id}`, { ...s, enabled: !s.enabled }); load(); };
  const delSource = async (s) => { await api.delete(`/web-sources/${s.id}`); load(); };


  return (
    <Layout nav={getNavFor(user)} showTestButton={has(user, "test_dispenser")}>
      <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground">Resources &amp; Rules</h1>
      <p className="mt-1 text-sm text-muted-foreground">Manage reference books, the web-crawl allowlist, and how answers are presented to dispensers.</p>

      <Tabs defaultValue="resources" className="mt-8">
        <TabsList>
          <TabsTrigger value="resources" data-testid="tab-resources"><FileText className="mr-1.5 h-4 w-4" />Reference Books</TabsTrigger>
          <TabsTrigger value="web" data-testid="tab-web"><Globe className="mr-1.5 h-4 w-4" />Web Allowlist</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules"><BookOpen className="mr-1.5 h-4 w-4" />Display Rules</TabsTrigger>
        </TabsList>

        {/* Resources */}
        <TabsContent value="resources" className="mt-6">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Lower precedence number = consulted first (higher priority).</p>
            {has(user, "manage_resources") && (
              <Dialog open={openUpload} onOpenChange={setOpenUpload}>
                <DialogTrigger asChild><Button data-testid="hod-upload-pdf-button"><Upload className="mr-1.5 h-4 w-4" />Upload PDF</Button></DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto canda-scroll">
                  <DialogHeader><DialogTitle className="font-heading">Upload reference book</DialogTitle></DialogHeader>
                  <div className="space-y-4 py-2">
                    <div className="space-y-2"><Label>Title</Label><Input value={up.title} onChange={(e) => setUp({ ...up, title: e.target.value })} placeholder="e.g. BNF 84" data-testid="upload-title-input" /></div>
                    <div className="space-y-2"><Label>Category</Label><Input value={up.category} onChange={(e) => setUp({ ...up, category: e.target.value })} data-testid="upload-category-input" /></div>
                    <div className="space-y-2"><Label>Precedence (1 = highest)</Label><Input type="number" value={up.precedence} onChange={(e) => setUp({ ...up, precedence: Number(e.target.value) })} data-testid="upload-precedence-input" /></div>
                    <div className="space-y-2"><Label>Usage rules (when/where to use this book)</Label><Textarea value={up.usage_rules} onChange={(e) => setUp({ ...up, usage_rules: e.target.value })} placeholder="e.g. Use for dosage & interactions; do not use for local malaria protocols." data-testid="upload-rules-input" /></div>
                    <div className="flex items-center justify-between rounded-lg border border-border p-3">
                      <div><div className="text-sm font-medium text-foreground">Primary reference</div><div className="text-xs text-muted-foreground">Consulted first, before all others (e.g. the BNF).</div></div>
                      <Switch checked={up.is_primary} onCheckedChange={(v) => setUp({ ...up, is_primary: v })} data-testid="upload-primary-switch" />
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <div className="space-y-2"><Label>Page offset</Label><Input type="number" value={up.page_offset} onChange={(e) => setUp({ ...up, page_offset: Number(e.target.value) })} data-testid="upload-offset-input" /><p className="text-[11px] text-muted-foreground">BNF: 19</p></div>
                      <div className="space-y-2"><Label>Drug content from (printed)</Label><Input type="number" value={up.drug_content_start} onChange={(e) => setUp({ ...up, drug_content_start: Number(e.target.value) })} data-testid="upload-drugstart-input" /><p className="text-[11px] text-muted-foreground">BNF: 37</p></div>
                      <div className="space-y-2"><Label>Interactions from (printed)</Label><Input type="number" value={up.interactions_start} onChange={(e) => setUp({ ...up, interactions_start: Number(e.target.value) })} data-testid="upload-interactions-input" /><p className="text-[11px] text-muted-foreground">BNF: 1515</p></div>
                    </div>
                    <div className="space-y-2"><Label>PDF file</Label><Input type="file" accept="application/pdf" ref={fileRef} data-testid="upload-file-input" /></div>
                  </div>
                  <DialogFooter><Button onClick={doUpload} disabled={uploading} data-testid="upload-submit">{uploading ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Indexing…</> : "Upload & index"}</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="space-y-3">
            {resources.map((r) => (
              <Card key={r.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`resource-row-${r.id}`}>
                <div className="flex items-center gap-3">
                  <div className="flex flex-col items-center">
                    {has(user, "manage_resources") && <button onClick={() => changePrecedence(r, -5)} className="text-muted-foreground hover:text-sky-600"><ArrowUp className="h-4 w-4" /></button>}
                    <Badge variant="outline" className="my-0.5 font-mono-c text-xs">#{r.precedence}</Badge>
                    {has(user, "manage_resources") && <button onClick={() => changePrecedence(r, 5)} className="text-muted-foreground hover:text-sky-600"><ArrowDown className="h-4 w-4" /></button>}
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-950"><FileText className="h-5 w-5" /></div>
                  <div>
                    <div className="flex items-center gap-2 font-heading font-bold text-foreground">{r.title}{r.is_primary && <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 dark:bg-sky-950 dark:text-sky-300 text-[10px]">Primary</Badge>}{r.is_sample && <Badge variant="outline" className="text-[10px]">sample</Badge>}</div>
                    <div className="text-xs text-muted-foreground">{r.category} · {r.page_count} pages indexed{r.page_offset ? ` · offset ${r.page_offset}` : ""}{r.usage_rules ? ` · ${r.usage_rules}` : ""}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 pl-14 sm:pl-0">
                  <div className="flex items-center gap-1.5"><span className="text-xs text-muted-foreground">Active</span><Switch checked={r.is_active} onCheckedChange={() => toggleResource(r)} disabled={!has(user, "manage_resources")} data-testid={`toggle-resource-${r.id}`} /></div>
                  {has(user, "manage_resources") && !r.is_sample && <Button variant="ghost" size="icon" onClick={() => reindexResource(r)} disabled={reindexing === r.id} title="Re-index pages" data-testid={`reindex-resource-${r.id}`}>{reindexing === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 text-emerald-600" />}</Button>}
                  {has(user, "manage_resources") && <Button variant="ghost" size="icon" onClick={() => setEditRes({ ...r })} data-testid={`edit-resource-${r.id}`}><Pencil className="h-4 w-4 text-sky-600" /></Button>}
                  {has(user, "manage_resources") && <Button variant="ghost" size="icon" onClick={() => delResource(r)} data-testid={`delete-resource-${r.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Web sources */}
        <TabsContent value="web" className="mt-6">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Only these approved domains are crawled for supplementary info.</p>
            {has(user, "manage_web_sources") && (
              <Dialog open={openSrc} onOpenChange={setOpenSrc}>
                <DialogTrigger asChild><Button data-testid="add-web-source-button"><Plus className="mr-1.5 h-4 w-4" />Add source</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-heading">Approve a web source</DialogTitle></DialogHeader>
                  <div className="space-y-4 py-2">
                    <div className="space-y-2"><Label>Name</Label><Input value={srcForm.name} onChange={(e) => setSrcForm({ ...srcForm, name: e.target.value })} data-testid="source-name-input" /></div>
                    <div className="space-y-2"><Label>URL</Label><Input value={srcForm.url} onChange={(e) => setSrcForm({ ...srcForm, url: e.target.value })} placeholder="https://…" data-testid="source-url-input" /></div>
                    <div className="space-y-2"><Label>Precedence</Label><Input type="number" value={srcForm.precedence} onChange={(e) => setSrcForm({ ...srcForm, precedence: Number(e.target.value) })} data-testid="source-precedence-input" /></div>
                    <div className="space-y-2"><Label>Notes</Label><Textarea value={srcForm.notes} onChange={(e) => setSrcForm({ ...srcForm, notes: e.target.value })} /></div>
                  </div>
                  <DialogFooter><Button onClick={addSource} data-testid="source-submit">Add to allowlist</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="space-y-3">
            {sources.length === 0 && <Card className="p-6 text-center text-sm text-muted-foreground">No approved web sources yet.</Card>}
            {sources.map((s) => (
              <Card key={s.id} className="flex items-center justify-between p-4" data-testid={`source-row-${s.id}`}>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-50 text-sky-600 dark:bg-sky-950"><Globe className="h-5 w-5" /></div>
                  <div>
                    <div className="font-heading font-bold text-foreground">{s.name} <Badge variant="outline" className="font-mono-c text-[10px]">#{s.precedence}</Badge></div>
                    <a href={s.url} target="_blank" rel="noreferrer" className="text-xs text-sky-600 hover:underline">{s.domain || s.url}</a>
                    {s.notes && <div className="text-xs text-muted-foreground">{s.notes}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={s.enabled} onCheckedChange={() => toggleSource(s)} disabled={!has(user, "manage_web_sources")} data-testid={`toggle-source-${s.id}`} />
                  {has(user, "manage_web_sources") && <Button variant="ghost" size="icon" onClick={() => delSource(s)}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Rules */}
        <TabsContent value="rules" className="mt-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card className="p-6">
              <div className="flex items-center gap-1.5">
                <BookOpen className="h-4 w-4 text-sky-600" />
                <h3 className="font-heading text-lg font-bold text-foreground">How answers are produced</h3>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Dispenser results are the exact text from your active reference books, shown verbatim with
                printed-page citations — nothing is generated or paraphrased. Control what surfaces first with
                the per-topic reference order and each book's precedence.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2"><BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" /> Verbatim passages, grouped by the book's own sections.</li>
                <li className="flex items-start gap-2"><ListOrdered className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Order controlled by precedence &amp; per-topic rules.</li>
                <li className="flex items-start gap-2"><RefreshCw className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" /> Pooled answers refresh automatically whenever you change the books.</li>
              </ul>
            </Card>

            <Card className="p-6">
              <div className="flex items-center gap-1.5">
                <ListOrdered className="h-4 w-4 text-emerald-600" />
                <h3 className="font-heading text-lg font-bold text-foreground">Per-topic reference order</h3>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">Set which reference is consulted first for each type of question. Top of each list = highest priority for that topic.</p>
              <div className="mt-4 space-y-4">
                {TOPICS.map((topic) => (
                  <div key={topic} className="rounded-xl border border-border p-3" data-testid={`topic-order-${topic.replace(/\s/g, "-")}`}>
                    <div className="mb-2 text-sm font-semibold text-foreground">{topic}</div>
                    <div className="space-y-1.5">
                      {(topicOrders[topic] || []).length === 0 && <p className="text-xs text-muted-foreground">No active references.</p>}
                      {(topicOrders[topic] || []).map((rid, idx) => {
                        const r = resources.find((x) => x.id === rid);
                        if (!r) return null;
                        return (
                          <div key={rid} className="flex items-center justify-between gap-2 rounded-lg bg-muted/40 px-2.5 py-1.5">
                            <div className="flex items-center gap-2">
                              <span className="font-mono-c text-[10px] text-muted-foreground">{idx + 1}</span>
                              <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="text-sm text-foreground">{r.title}</span>
                            </div>
                            {has(user, "manage_rules") && (
                              <div className="flex items-center">
                                <button onClick={() => moveTopic(topic, idx, -1)} className="text-muted-foreground hover:text-sky-600" data-testid={`topic-up-${topic.replace(/\s/g, "-")}-${idx}`}><ArrowUp className="h-4 w-4" /></button>
                                <button onClick={() => moveTopic(topic, idx, 1)} className="text-muted-foreground hover:text-sky-600" data-testid={`topic-down-${topic.replace(/\s/g, "-")}-${idx}`}><ArrowDown className="h-4 w-4" /></button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              {has(user, "manage_rules") && (
                <Button className="mt-4" onClick={saveTopicOrder} disabled={savingTopic} data-testid="save-topic-order-button">
                  {savingTopic ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}Save topic order
                </Button>
              )}
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!editRes} onOpenChange={(o) => !o && setEditRes(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto canda-scroll" data-testid="edit-resource-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit reference</DialogTitle></DialogHeader>
          {editRes && (
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Title</Label><Input value={editRes.title} onChange={(e) => setEditRes({ ...editRes, title: e.target.value })} data-testid="edit-title-input" /></div>
              <div className="space-y-2"><Label>Category</Label><Input value={editRes.category || ""} onChange={(e) => setEditRes({ ...editRes, category: e.target.value })} data-testid="edit-category-input" /></div>
              <div className="space-y-2"><Label>Precedence (1 = highest)</Label><Input type="number" value={editRes.precedence} onChange={(e) => setEditRes({ ...editRes, precedence: e.target.value })} data-testid="edit-precedence-input" /></div>
              <div className="space-y-2"><Label>Usage rules</Label><Textarea value={editRes.usage_rules || ""} onChange={(e) => setEditRes({ ...editRes, usage_rules: e.target.value })} data-testid="edit-rules-input" /></div>
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div><div className="text-sm font-medium text-foreground">Primary reference</div><div className="text-xs text-muted-foreground">Consulted first, before all others.</div></div>
                <Switch checked={!!editRes.is_primary} onCheckedChange={(v) => setEditRes({ ...editRes, is_primary: v })} data-testid="edit-primary-switch" />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="space-y-2"><Label>Page offset</Label><Input type="number" value={editRes.page_offset || 0} onChange={(e) => setEditRes({ ...editRes, page_offset: e.target.value })} data-testid="edit-offset-input" /></div>
                <div className="space-y-2"><Label>Drug content from</Label><Input type="number" value={editRes.drug_content_start || 0} onChange={(e) => setEditRes({ ...editRes, drug_content_start: e.target.value })} data-testid="edit-drugstart-input" /></div>
                <div className="space-y-2"><Label>Interactions from</Label><Input type="number" value={editRes.interactions_start || 0} onChange={(e) => setEditRes({ ...editRes, interactions_start: e.target.value })} data-testid="edit-interactions-input" /></div>
              </div>
              {!editRes.is_sample && <p className="text-xs text-muted-foreground">After changing page settings, use the re-index button on the reference to refresh its indexed pages.</p>}
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} disabled={savingEdit} data-testid="edit-resource-submit">{savingEdit ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
